import sumBy from 'lodash/sumBy';

const COUPON_TYPES = {
  PERCENTAGE: 'percent',
  FIXED_CART: 'fixed_cart',
  FIXED_PRODUCT: 'fixed_product'
};

class CouponHandler {
  constructor(cart, coupon) {
    this.cart = cart;
    this.coupon = coupon;
    this.minimumSpent = parseFloat(coupon.minimum_amount) || 0;
    this.maximumSpent = parseFloat(coupon.maximum_amount) || Infinity;
    this.individualUseOnly = coupon.individualUse || false;
    this.excludeSaleItems = coupon.exclude_sale_items || false;
    this.productIds = coupon.productIds || [];
    this.excludedProductIds = coupon.excludedProductIds || [];
    this.productCategories = coupon.product_categories || [];
    this.excludedProductCategories = coupon.excluded_product_categories || [];
    this.usageLimitPerUser = coupon.usage_limit_per_user || null;
    this.usageLimit = coupon.usageLimit || null;
    this.expiryDate = coupon.dateExpires ? new Date(coupon.dateExpires) : null;
    this.allowedEmails = coupon.allowed_emails || [];

  }

  validateCoupon(userEmail) {

    // Email validation
    if (this.allowedEmails.length > 0 && !this.allowedEmails.includes(userEmail)) {
      throw new Error('This coupon is not valid for your email address');
    }

    if (this.usageLimit !== null && this.coupon.usage_count >= this.usageLimit) {
      throw new Error('This coupon has reached its usage limit');
    }
  
    if (this.usageLimitPerUser !== null && this.coupon.usage_count_per_user >= this.usageLimitPerUser) {
      throw new Error('You have reached the maximum usage for this coupon');
    }

    if (this.expiryDate && new Date() > this.expiryDate) {
      throw new Error('This coupon has expired');
    }

    const cartTotal = this.getCartTotal();
    if (cartTotal < this.minimumSpent) {
      throw new Error(`A minimum spend of ${this.minimumSpent} is required`);
    }

    if (cartTotal > this.maximumSpent) {
      throw new Error(`This coupon cannot be used for orders over ${this.maximumSpent}`);
    }

    // Validate product restrictions
    if (this.hasProductRestrictions() && !this.validateProductRestrictions()) {
      throw new Error('Cart does not contain all required products or categories');
    }

    if (this.hasExcludedProducts()) {
      throw new Error('This coupon cannot be used with some items in your cart');
    }

    if (this.excludeSaleItems && this.hasDiscountedItems()) {
      throw new Error('This coupon cannot be used with sale items');
    }

    if (this.individualUseOnly && this.cart.some(item => item.applied_coupons?.length > 0)) {
      throw new Error('This coupon cannot be used with other coupons');
    }

    return true;
  }

  hasProductRestrictions() {
    return this.productIds.length > 0 || this.productCategories.length > 0;
  }

  validateProductRestrictions() {
    if (!this.hasProductRestrictions()) return true;

    const requiredProducts = new Set(this.productIds);
    const requiredCategories = new Set(this.productCategories);
    
    // If both products and categories are specified, either condition should satisfy
    if (this.productIds.length > 0 && this.productCategories.length > 0) {
      return this.cart.some(item => 
        requiredProducts.has(item.id) || 
        requiredProducts.has(item.variation_id) ||
        item.categories?.some(cat => requiredCategories.has(cat))
      );
    }

    // Check products
    if (this.productIds.length > 0) {
      return this.productIds.every(id => 
        this.cart.some(item => item.id === id || item.variation_id === id)
      );
    }

    // Check categories
    if (this.productCategories.length > 0) {
      return this.productCategories.every(category =>
        this.cart.some(item => item.categories?.includes(category))
      );
    }

    return true;
  }

  calculateDiscount() {
    try {
      this.validateCoupon();

      switch (this.coupon.type) {
        case COUPON_TYPES.PERCENTAGE:
          return this.calculatePercentageDiscount();
        case COUPON_TYPES.FIXED_CART:
          return this.calculateFixedCartDiscount();
        case COUPON_TYPES.FIXED_PRODUCT:
          return this.calculateFixedProductDiscount();
        default:
          throw new Error('Invalid coupon type');
      }
    } catch (error) {
      throw error;
    }
  }

  calculatePercentageDiscount() {
    const applicableItems = this.getApplicableItems();
    const discountPercentage = parseFloat(this.coupon.amount);
    
    const totalDiscount = applicableItems.reduce((sum, item) => {
      const itemTotal = parseFloat(item.price) * item.quantity;
      const itemDiscount = (itemTotal * discountPercentage) / 100;
      return sum + itemDiscount;
    }, 0);

    return Math.min(totalDiscount, this.coupon.maximum_discount || Infinity);
  }

  calculateFixedCartDiscount() {
    const applicableItems = this.getApplicableItems();
    const applicableTotal = sumBy(applicableItems, item => 
      parseFloat(item.price) * item.quantity
    );
    return Math.min(parseFloat(this.coupon.amount), applicableTotal);
  }

  calculateFixedProductDiscount() {
    const applicableItems = this.getApplicableItems();
    
    let totalDiscount = 0;
    
    applicableItems.forEach(item => {
      const discountPerItem = Math.min(
        parseFloat(this.coupon.amount), 
        parseFloat(item.price)
      );
      const itemDiscount = discountPerItem * item.quantity;
      totalDiscount += itemDiscount;
    });
  
    return totalDiscount;
  }

  getApplicableItems() {
    // console.log("Starting getApplicableItems with productIds:", this.productIds);
    
    const filteredItems = this.cart.filter(item => {
      // console.log(`Checking item ${item.id}/${item.variation_id}`);
      
      // If we have product restrictions, only allow those specific products
      if (this.productIds.length > 0) {
        const isIncluded = this.productIds.includes(item.id) || this.productIds.includes(item.variation_id);
        // console.log(`Product restriction check: ${isIncluded}`);
        if (!isIncluded) return false;
      }
  
      // Already filtered by product restrictions, now check exclusions
      if (this.excludedProductIds.includes(item.id) || 
          this.excludedProductIds.includes(item.variation_id)) {
        // console.log("Product is excluded");
        return false;
      }
  
      if (this.excludeSaleItems && item.on_sale) {
        // console.log("Product is on sale and excluded");
        return false;
      }
  
      // console.log(`Product ${item.id} is applicable`);
      return true;
    });
  
    // console.log("Final filtered items:", filteredItems);
    return filteredItems;
  }

  hasExcludedProducts() {
    return this.cart.some(item => 
      this.excludedProductIds.includes(item.id) ||
      this.excludedProductIds.includes(item.variation_id) ||
      item.categories?.some(cat => this.excludedProductCategories.includes(cat))
    );
  }

  hasDiscountedItems() {
    return this.cart.some(item => item.on_sale);
  }

  getCartTotal() {
    return sumBy(this.cart, item => parseFloat(item.price) * item.quantity);
  }
}

export default CouponHandler;
